// Admin-only delivery of generated documents (CV, revamped CV, ATS report)
// straight into a client's own portal account.
//
// Flow: admin supplies the client's email → we look up a matching profile →
// if an account exists the file is stored in the client's `cv-library` folder
// and indexed in `cv_library`, so it appears under "My files & downloads".
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { assertAdmin } from "./_admin-guard";

const BUCKET = "cv-library";

const FileSchema = z.object({
  filename: z.string().min(1).max(200),
  contentBase64: z.string().min(10),
  contentType: z.string().max(120).default("application/pdf"),
  label: z.string().trim().max(120).default(""),
});

/** Does a portal account exist for this email? Used to show admins a hint. */
export const lookupClientAccount = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) =>
    z.object({ email: z.string().trim().toLowerCase().email() }).parse(i),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context as any);
    const { supabaseAdmin } = await import(
      "@/integrations/supabase/client.server"
    );
    const { data: prof } = await supabaseAdmin
      .from("profiles")
      .select("id, full_name, email")
      .ilike("email", data.email)
      .maybeSingle();
    return {
      exists: Boolean(prof?.id),
      userId: (prof as any)?.id ?? null,
      fullName: (prof as any)?.full_name ?? null,
    };
  });

/**
 * Push one or more generated documents into the client's portal account.
 * Silently reports `delivered: false` when no account exists — the admin can
 * still email the files.
 */
export const deliverDocsToClientAccount = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) =>
    z
      .object({
        email: z.string().trim().toLowerCase().email(),
        clientName: z.string().trim().max(160).optional(),
        source: z.enum(["cv_builder", "ats_scan", "ats_revamp"]),
        files: z.array(FileSchema).min(1).max(5),
      })
      .parse(i),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context as any);
    const { supabaseAdmin } = await import(
      "@/integrations/supabase/client.server"
    );

    const { data: prof } = await supabaseAdmin
      .from("profiles")
      .select("id, full_name")
      .ilike("email", data.email)
      .maybeSingle();
    const userId = (prof as any)?.id as string | undefined;
    if (!userId) {
      return { delivered: false, reason: "no_account" as const, saved: 0 };
    }

    const stamp = Date.now();
    let saved = 0;
    const names: string[] = [];

    for (const [idx, file] of data.files.entries()) {
      try {
        const bin = atob(file.contentBase64);
        const bytes = new Uint8Array(bin.length);
        for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
        const safe = file.filename.replace(/[^\w.\-]+/g, "_").slice(0, 120);
        const path = `${userId}/from-ndlcs/${stamp}-${idx}-${safe}`;

        const { error: upErr } = await supabaseAdmin.storage
          .from(BUCKET)
          .upload(path, bytes, {
            contentType: file.contentType || "application/pdf",
            upsert: true,
          });
        if (upErr) throw new Error(upErr.message);

        const { error: rowErr } = await supabaseAdmin.from("cv_library").insert({
          user_id: userId,
          kind: "cv",
          storage_path: path,
          original_filename: safe,
          mime_type: file.contentType || "application/pdf",
          size_bytes: bytes.length,
          label: file.label || "Prepared by NDLCS",
        });
        if (rowErr) throw new Error(rowErr.message);
        saved += 1;
        names.push(safe);
      } catch (err) {
        console.error("[client-delivery] file failed", err);
      }
    }

    if (saved > 0) {
      const { recordClientEvent } = await import("@/lib/client-onboarding.server");
      await recordClientEvent({
        email: data.email,
        userId,
        type: "document_delivered",
        title:
          data.source === "cv_builder"
            ? "CV added to your portal"
            : data.source === "ats_revamp"
              ? "Revamped CV & ATS report added to your portal"
              : "ATS report added to your portal",
        detail: `${saved} document(s) saved to your account: ${names.join(", ")}.`,
        metadata: { source: data.source, files: names },
        actor: "admin",
      });
    }

    return { delivered: saved > 0, reason: null, saved, userId };
  });
